import React, { useCallback, useEffect, useRef, useState } from 'react';
import cls from 'classnames';
import { ICONS } from '../../constants/icons';
import makeBodyText from '../../utils/makeText';
import makeIconName from '../../utils/makeIconName';
import { generateYears, generateDaysOfMonth, getMonthDetails, formatDate } from '../../utils/dateUtils';
import OrderPicker from './sections/OrderPicker';
import YearsPicker from './sections/YearsPicker';
import MonthsPicker from './sections/MonthsPicker';
import DayPicker from './sections/DayPicker';
import styles from './index.module.scss';
import Tooltip from '../../components/Tooltip';
const days = generateDaysOfMonth();
const DatePicker = ({ label, placeholder, picker = 'day', defaultValue, isDisabled, isDropDownStrictWidth, isFullWidth, isError, description, onChange, onClose, onClear, name, isPayCheck = false, minDate, maxDate, earlySelectedDates = [], showToolTip }) => {
    const [isOpen, toggleOpen] = useState(false);
    const [year, setYear] = useState(new Date().getFullYear());
    const [month, setMonth] = useState(new Date().getMonth());
    const [value, setValue] = useState(null);
    const [monthDays, setMonthDays] = useState(null);
    const [yearsIndex, setYearsIndex] = useState(0);
    const [years, setYears] = useState([]);
    const [mode, setMode] = useState('date');
    const [isHovered, setIsHovered] = useState(false);
    const ref = useRef();
    const _handleOut = useCallback(() => {
        toggleOpen(false);
        onClose && onClose();
    }, [onClose]);
    const _handleClickOutside = useCallback((e) => {
        if (ref.current && !ref.current.contains(e.target)) {
            _handleOut();
        }
    }, [_handleOut]);
    const setZeroHours = (date) => {
        return new Date(date.setHours(0, 0, 0, 0));
    };
    useEffect(() => {
        document.addEventListener('click', _handleClickOutside, true);
        return () => {
            document.removeEventListener('click', _handleClickOutside, true);
        };
    }, [_handleClickOutside]);
    useEffect(() => {
        setMonthDays(getMonthDetails(year, month));
    }, [month, year]);
    useEffect(() => {
        setYears(generateYears(year, yearsIndex));
    }, [year, yearsIndex]);
    useEffect(() => {
        if (!defaultValue)
            return;
        if (picker === 'day') {
            let date = new Date(defaultValue);
            const tzOffsetInHours = date.getTimezoneOffset() / 60;
            date.setHours(date.getHours() + tzOffsetInHours);
            if (date && date instanceof Date && !isNaN(date.getTime())) {
                date = setZeroHours(date);
                setValue(date.setHours(0, 0, 0, 0));
                setYear(date.getFullYear());
                setMonth(date.getMonth());
            }
        }
        else if (picker === 'order') {
            if (!isNaN(+defaultValue) && ((+defaultValue >= 1 && +defaultValue <= 31) || +defaultValue === -1)) {
                setValue(+defaultValue);
            }
        }
    }, [defaultValue, picker]);
    const _handleEnter = () => {
        if (picker === 'day') {
            let date = value ? new Date(value) : new Date();
            date = setZeroHours(date);
            setMonth(date.getMonth());
            setYear(date.getFullYear());
        }
        setMode('date');
        toggleOpen(true);
    };
    const _changeMonth = (val) => {
        let _month = month + val;
        let _year = year;
        if (_month < 0) {
            _month = 11;
            _year -= 1;
        }
        else if (_month > 11) {
            _month = 0;
            _year += 1;
        }
        setYear(_year);
        setMonth(_month);
    };
    const _handleClear = () => {
        setValue(null);
        _handleOut();
        onClear && onClear();
    };
    const _getSelectedItem = () => {
        var _a;
        if (!value) {
            return placeholder;
        }
        if (picker === 'order') {
            return (React.createElement(React.Fragment, null,
                React.createElement("span", null, ((_a = days.find((day) => +day.value === +value)) === null || _a === void 0 ? void 0 : _a.label) || 'Last day'),
                " of each month"));
        }
        return React.createElement("span", null, formatDate(value));
    };
    const _handleSelect = (val) => {
        if (picker === 'order') {
            setValue(val.value);
            onChange(val);
        }
        else if (picker === 'day') {
            setValue(val);
            onChange(formatDate(val));
        }
        _handleOut();
    };
    const setHover = () => {
        setIsHovered(!isHovered);
    };
    const _renderInput = () => {
        return React.createElement("input", { readOnly: true, type: 'hidden', name: name, value: formatDate(new Date(value)) });
    };
    const _getContent = () => {
        if (picker === 'order') {
            return React.createElement(OrderPicker, { handleSelect: _handleSelect, value: value, days: days });
        }
        if (mode === 'month') {
            return React.createElement(MonthsPicker, { setMode: setMode, setMonth: setMonth, month: month });
        }
        if (mode === 'year') {
            return (React.createElement(YearsPicker, { setMode: setMode, setYear: setYear, setYearsIndex: setYearsIndex, years: years, year: year }));
        }
        return (React.createElement(DayPicker, { name: name, earlySelectedDates: earlySelectedDates, isPayCheck: isPayCheck, changeMonth: _changeMonth, setMode: setMode, setYearsIndex: setYearsIndex, handleSelect: _handleSelect, value: value, year: year, month: month, monthDays: monthDays, minDate: minDate, maxDate: maxDate }));
    };
    const _renderCalendar = () => {
        if (!isOpen) {
            return null;
        }
        return (React.createElement("div", { className: `${styles.dropdown} ${isDropDownStrictWidth ? styles.fixedWidth : ''}` },
            React.createElement("div", { className: styles.content }, _getContent())));
    };
    const _renderClearButton = () => {
        if (!value || isPayCheck) {
            return null;
        }
        return (React.createElement("div", { tabIndex: 0, role: 'button', onKeyDown: undefined, "aria-label": 'Clear input field', className: cls(makeIconName(ICONS.x24.cross, 'x24'), styles.supportBtn, styles.extraIconMargin), onClick: _handleClear }));
    };
    const _renderLabel = () => {
        return React.createElement("span", { className: cls(makeBodyText('M'), styles.label) }, label);
    };
    const renderTooltip = () => {
        if (!isDisabled && !showToolTip) {
            return null;
        }
        return React.createElement(Tooltip, { useClick: true, withoutChild: true, forceShow: isHovered, label: 'This field isn\'t available' });
    };
    const _renderSelect = () => {
        return (React.createElement(React.Fragment, null,
            React.createElement("button", { type: 'button', className: cls(styles.select, {
                    [styles.active]: isOpen,
                    [styles.selected]: value,
                    [styles.disabled]: isDisabled,
                    [styles.error]: isError
                }), onClick: _handleEnter, onFocus: _handleEnter, disabled: isDisabled },
                value && _renderLabel(),
                React.createElement("span", { className: cls(makeBodyText('L'), styles.text) }, _getSelectedItem()),
                _renderClearButton(),
                React.createElement("i", { "aria-label": 'Calendar', className: cls(makeIconName(ICONS.x24.calendar, 'x24'), styles.supportBtn, styles.icon) })),
            description && (React.createElement("p", { id: `${name}-err-text`, className: `${makeBodyText('S')} ${styles.description} ${isError ? styles.withError : ''}` }, description))));
    };
    return (React.createElement("div", Object.assign({ className: cls(styles.wrapper, { [styles.fullWidth]: isFullWidth }), ref: ref }, (isDisabled && showToolTip ?
        {
            onMouseEnter: setHover,
            onMouseLeave: setHover,
            onFocus: setHover,
            onBlur: setHover
        } :
        {})),
        renderTooltip(),
        _renderSelect(),
        _renderCalendar(),
        _renderInput()));
};
export default DatePicker;
